<template>
    <DCard padding>
        <DRow
            align-horizontal="start"
            align-vertical="end"
            class="mb-3"
        >
            <DCol lg="2">
                <DDateFormatInput
                    id="date-to"
                    v-model="filterData.dateTo"
                    format="YYYY-MM-DD"
                    :label="__('contracts.pending.date_to')"
                    type="date"
                    @keydown.enter.exact.prevent="filter(contracts.current_page, filterData)"
                />
            </DCol>
            <DCol lg="2">
                <DDateFormatInput
                    id="date-from"
                    v-model="filterData.dateFrom"
                    format="YYYY-MM-DD"
                    :label="__('contracts.pending.date_from')"
                    type="date"
                    @keydown.enter.exact.prevent="filter(contracts.current_page, filterData)"
                />
            </DCol>
            <DCol lg="3">
                <DInput
                    id="search"
                    v-model="filterData.productName"
                    :label="__('customers.contracts.search_product')"
                    @keydown.enter.exact.prevent="filter(contracts.current_page, filterData)"
                />
            </DCol>
            <DCol lg="2">
                <DInput
                    id="customer"
                    v-model="filterData.customer"
                    :label="__('contracts.pending.customer_id')"
                    type="number"
                    pattern="^\d*$"
                    min="1"
                    @keydown.enter.exact.prevent="filter(contracts.current_page, filterData)"
                />
            </DCol>
            <DCol
                lg="3"
                class="d-flex gap-2"
            >
                <DButton
                    id="filter-apply"
                    :label="__('messages.actions.filter')"
                    @click="filter(contracts.current_page, filterData)"
                />
                <DButton
                    id="filter-reset"
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>

        <DRow>
            <DCol lg="12">
                <DTable
                    :items="contracts.items"
                    :fields="fields"
                >
                    <template #customer="{item}">
                        <td v-if="userPermissions.includes('customer.view')">
                            <Link :href="customerProfileRoute(item)">
                                {{ getCustomerName(item) }}
                            </Link>
                        </td>
                        <td v-else>
                            {{ getCustomerName(item) }}
                        </td>
                    </template>
                    <template #jira_ticket_url="{item}">
                        <td>
                            <a
                                :href="item.jira_ticket_url"
                                target="_blank"
                            >
                                {{ item.jira_ticket_id }}
                            </a>
                        </td>
                    </template>
                    <template #created="item">
                        <td>
                            {{ setDateFormat(item.created) }}
                        </td>
                    </template>
                    <template #actions="{item}">
                        <td>
                            <DButton
                                icon="pencil"
                                mini
                                @click="$inertia.get(editContract(item))"
                            />
                            <DButton
                                v-if="canActivate && item.status === 'wait_for_activation'"
                                icon="check"
                                mini
                                :label="__('customers.contracts.set_active')"
                                @click="showActivateContractModal(item)"
                            />
                            <DButton
                                v-if="userPermissions.includes('order.view')"
                                icon="eye"
                                mini
                                :label="__('customers.contracts.view_order')"
                                @click="seeOrder(item)"
                            />
                        </td>
                    </template>
                </DTable>
                <DPagination
                    v-if="contracts.last_page > 1"
                    :active-page="contracts.current_page"
                    :pages="contracts.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
                <DModal
                    v-model:show="showMissingOrderModal"
                    :title="__('customers.contracts.order.missing_order_title')"
                    :message="__('customers.contracts.order.missing_order_message')"
                    is-error
                    @close="showMissingOrderModal = false"
                />
            </DCol>
        </DRow>
        <ActivateContractModal
            v-if="showActivateModal"
            :contract="selectedContract"
            :show-modal="showActivateModal"
            @close-modal="showActivateModal = false"
        />
    </DCard>
</template>

<script>
import ActivateContractModal from '~@/Components/Customers/Contracts/Modals/ActivateContract.vue'
import orderNumber from '~@/Mixins/orderNumber'
import _ from 'lodash'

export default {
    name: 'PendingContractIndex',
    components: { ActivateContractModal },
    mixins: [orderNumber],
    props: {
        contracts: {
            type: Object,
            default: () => { return {} }
        },
        filters: {
            type: Object,
            default: () => { return {} }
        }
    },
    data () {
        return {
            fields: [
                { key: 'customer', label: this.__('contracts.pending.customer') },
                { key: 'contract_id', label: this.__('contracts.pending.contract_id') },
                { key: 'contract_number', label: this.__('contracts.pending.contract_number') },
                { key: 'product_name', label: this.__('contracts.pending.product_name') },
                { key: 'created', label: this.__('contracts.pending.created') },
                { key: 'status', label: this.__('contracts.pending.status') },
                { key: 'jira_ticket_url', label: this.__('contracts.pending.jira') },
                { key: 'actions', label: '' }
            ],
            showMissingOrderModal: false,
            showActivateModal: false,
            selectedContract: null,
            filterData: _.cloneDeep(this.filters)
        }
    },
    computed: {
        canActivate () {
            return this.userPermissions.includes('contract.pending')
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        }
    },
    methods: {
        customerProfileRoute (contract) {
            return this.$route('customers.customers.show', { customer: contract.customer_id })
        },
        getCustomerName (contract) {
            if (contract.customer_company !== null && contract.customer_company !== '') {
                return contract.customer_full_name !== ''
                    ? `${contract.customer_full_name} (${contract.customer_company}) (${contract.customer_id})`
                    : `${contract.customer_company} (${contract.customer_id})`
            }

            return `${contract.customer_full_name} (${contract.customer_id})`
        },
        editContract (contract) {
            return this.$route('customers.customers.contracts.edit', {
                customer: contract.customer_id,
                contract: contract.contract_id
            })
        },
        seeOrder (contract) {
            if (contract.order_id < 1) {
                this.showMissingOrderModal = true

                return
            }

            return this.$inertia.get(this.$route('orders.details', this.orderDetailsRouteParams(contract.order)))
        },
        setActive (contract) {
            this.$inertia.post(this.$route('customers.customers.contracts.setActive', { customer: contract.customer_id, contract: contract.contract_id }))
        },
        changePage (page) {
            const query = {}

            if (page > 1) {
                query.page = page
            }
            this.filter(page, this.filterData)
            this.$inertia.get(this.$route('contracts.pending'), query)
        },
        filter (page, filters) {
            const queryParameters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))

            if (page > 1) {
                queryParameters.page = page
            }

            this.$inertia.get(this.$route('contracts.pending', { save_filters: true }), queryParameters)
        },
        resetFilters () {
            this.$inertia.get(this.$route('contracts.pending', {
                save_filters: true,
                default_filters: true
            }))
        },
        showActivateContractModal (contract) {
            this.selectedContract = contract
            this.selectedContract.id = this.selectedContract.contract_id
            this.showActivateModal = true
        }
    }
}
</script>
